import React, { ReactNode } from 'react';

export type MentionInfo = { nickname?: string; profile_url?: string; withdrawn?: boolean };

type RenderOptions = {
  /** { uuid: { nickname, profile_url, withdrawn } } — hydrates @[user:uuid] tokens */
  mentionsMap?: Record<string, MentionInfo>;
  onMentionClick?: (uuid: string) => void;
};

const URL_RE = /(https?:\/\/[^\s]+)/g;
const MENTION_RE = /@\[user:([0-9a-fA-F-]{8,64})\]/g;

type Token = { start: number; end: number; type: 'url' | 'mention'; value: string };

/**
 * Render community body/comment text, linkifying URLs and hydrating canonical
 * `@[user:uuid]` mentions into styled @nickname spans (커뮤니티 @Mention).
 * Withdrawn / unknown users render greyed and non-clickable.
 */
export const renderContentWithLinks = (
  content: string | undefined | null,
  options: RenderOptions = {},
): ReactNode => {
  if (!content) return null;
  const { mentionsMap = {}, onMentionClick } = options;

  const tokens: Token[] = [];
  let m: RegExpExecArray | null;

  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(content)) !== null) {
    tokens.push({ start: m.index, end: m.index + m[0].length, type: 'url', value: m[0] });
  }
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(content)) !== null) {
    tokens.push({ start: m.index, end: m.index + m[0].length, type: 'mention', value: m[1].toLowerCase() });
  }
  tokens.sort((a, b) => a.start - b.start);

  const out: ReactNode[] = [];
  let last = 0;

  tokens.forEach((t, i) => {
    if (t.start < last) return; // overlap guard
    if (t.start > last) out.push(content.slice(last, t.start));

    if (t.type === 'url') {
      out.push(
        <a
          key={`url-${i}`}
          href={t.value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline break-all transition-colors"
        >
          {t.value}
        </a>,
      );
    } else {
      const info = mentionsMap[t.value];
      const nickname = info?.nickname ?? 'unknown';
      const withdrawn = info ? info.withdrawn ?? false : true;
      out.push(
        <span
          key={`mention-${i}`}
          className={
            withdrawn
              ? 'text-gray-400'
              : 'text-blue-600 font-semibold cursor-pointer hover:underline'
          }
          onClick={withdrawn || !onMentionClick ? undefined : () => onMentionClick(t.value)}
        >
          @{nickname}
        </span>,
      );
    }
    last = t.end;
  });

  if (last < content.length) out.push(content.slice(last));
  return out;
};

/**
 * Preview variant of {@link renderContentWithLinks} for surfaces that are
 * themselves interactive (e.g. a clickable list card / <button>): hydrates
 * canonical `@[user:uuid]` mentions into styled @nickname spans but leaves URLs
 * as plain text, so no nested interactive <a> elements are produced. Mentions
 * are non-clickable here (tapping the card opens the post).
 */
export const renderMentionsOnly = (
  content: string | undefined | null,
  mentionsMap: Record<string, MentionInfo> = {},
): ReactNode => {
  if (!content) return null;

  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;

  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(content)) !== null) {
    if (m.index > last) out.push(content.slice(last, m.index));
    const uuid = m[1].toLowerCase();
    const info = mentionsMap[uuid];
    const nickname = info?.nickname ?? 'unknown';
    const withdrawn = info ? info.withdrawn ?? false : true;
    out.push(
      <span
        key={`m-${i}`}
        className={withdrawn ? 'text-gray-400' : 'text-blue-600 font-semibold'}
      >
        @{nickname}
      </span>,
    );
    last = m.index + m[0].length;
    i++;
  }

  if (last < content.length) out.push(content.slice(last));
  return out;
};
